import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ContactAdminModal from "../../components/ContactAdminModal";
import ReportModal from "../../components/ReportModal";
import ProfileModal from "../../components/ProfileModal";
import UserAdditionalInfoModal from "../../components/UserAdditionalInfoModal";
import AppointmentDetailsModal from "../../components/AppointmentDetailsModal";
import AppointmentSchedulerModal from "../../components/AppointmentSchedulerModal";
import DashboardSidebar from "../../components/DashboardSidebar";
import { generateFakePatientAppointments } from "./fakeDataGenerator";
import { checkIsLoggedIn, handleFetchResponse } from "./helpers";
import { BACKEND_URL } from "../../utils/config.js";
import "./Dashboard.css";

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || "/backend";

// Helper function to get formatted service names from appointment
const getServiceNames = (appointment) => {
  // Handle new format: service_names array
  if (appointment.service_names && Array.isArray(appointment.service_names) && appointment.service_names.length > 0) {
    return appointment.service_names.join(', ');
  } 
  
  // Handle old format: services array
  if (appointment.services && Array.isArray(appointment.services) && appointment.services.length > 0) {
    return appointment.services.join(', ');
  }

  // Handle old format: single service_name string
  if (appointment.service_name) {
    return appointment.service_name;
  } 
  
  // Handle old format: single service string
  if (appointment.service) {
    return appointment.service;
  }
  
  // Fallback for services as a string
  if (typeof appointment.services === 'string') {
    return appointment.services;
  }

  return 'N/A';
};

export default function PatientDashboard() {
    const navigate = useNavigate();
    const [upcomingAppointments, setUpcomingAppointments] = useState([]);
    // Get user data from localStorage
    const [user] = useState({
        username: localStorage.getItem('username') || '',
        user_id: localStorage.getItem('user_id') || '',
        role: localStorage.getItem('role') || ''
    });
    const [pastAppointments, setPastAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAppointment, setEditingAppointment] = useState(null);
    const [showDetailsMenu, setShowDetailsMenu] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
    const menuHideTimeoutRef = useRef(null);
    const [calendarView, setCalendarView] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
    const [availableServices, setAvailableServices] = useState([]);
    const [formData, setFormData] = useState({
        date: '',
        start_time: '',
        end_time: '',
        status: 'Pending',
        services: [],
        description: '',
        location: ''
    });
    const [error, setError] = useState(null);
    const [showContactModal, setShowContactModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState(null);
    
    // Upcoming appointments filter states
    const [upcomingStatusFilter, setUpcomingStatusFilter] = useState('all');
    const [upcomingServiceFilter, setUpcomingServiceFilter] = useState('all');
    const [availableStatuses, setAvailableStatuses] = useState([]);
    // Profile modal state
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showUserAdditionalInfoModal, setShowUserAdditionalInfoModal] = useState(false);
    const [profileData, setProfileData] = useState({
        email: '',
        username: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        date_of_birth: '',
        address: ''
    });
    const [profileFormData, setProfileFormData] = useState({
        email: '',
        username: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        date_of_birth: '',
        address: ''
    });
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState(null);
    const [profileSuccess, setProfileSuccess] = useState(null);

    useEffect(() => {
        const fetchAppointments = async () => {
            try {
                let isLoggedin = await checkIsLoggedIn();
                if (!isLoggedin || localStorage.length === 0) {
                    navigate(`${FRONTEND_URL}/login`);
                    localStorage.clear();
                }

                // Read USE_FAKE_DATA from environment, default to true if missing
                const USE_FAKE_DATA = process.env.REACT_APP_USE_FAKE_DATA !== 'false';
                
                let processedAppointments = [];
                
                if (USE_FAKE_DATA) {
                    // Use fake data for testing
                    processedAppointments = generateFakePatientAppointments();
                } else {
                    // Fetch from backend
                    const res = await fetch(`${BACKEND_URL}/appointments`, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        credentials: "include",
                    });

                    const checkedRes = await handleFetchResponse(res, navigate);
                    if (!checkedRes) return;

                    if (!checkedRes.ok) {
                        throw new Error(`Failed to fetch: ${checkedRes.status}`);
                    }

                    const data = await checkedRes.json();
                    processedAppointments = data.appointments.map(a => ({
                        ...a,
                        id: a.appointment_id 
                    }));
                }

                const now = new Date();
                const upcoming = processedAppointments.filter(a => new Date(a.start_date) >= now)
                    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
                const past = processedAppointments.filter(a => new Date(a.start_date) < now)
                    .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

                // Extract unique services and statuses from upcoming appointments
                const uniqueServices = new Set();
                const uniqueStatuses = new Set();
                upcoming.forEach(apt => {
                    // Handle new format: service_names array
                    if (apt.status) uniqueStatuses.add(apt.status);
                });

                setAvailableStatuses(Array.from(uniqueStatuses).sort());

                setUpcomingAppointments(upcoming);
                setPastAppointments(past);

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        const fetchServices = async () => {
            try {
                // Read USE_FAKE_DATA from environment, default to true if missing
                const USE_FAKE_DATA = process.env.REACT_APP_USE_FAKE_DATA !== 'false';
                
                if (USE_FAKE_DATA) {
                    // Use fake services for testing
                    setAvailableServices(['Personal Care', 'Meal Preparation', 'Medication Reminder', 'Companionship', 'Transportation']);
                } else {
                    const res = await fetch(`${BACKEND_URL}/others/services`, {
                        credentials: 'include'
                    });
                    const checkedRes = await handleFetchResponse(res, navigate);
                    if (checkedRes && checkedRes.ok) {
                        const services = await checkedRes.json();
                        // services should be an array of strings
                        setAvailableServices(Array.isArray(services) ? services : []);
                    }
                }
            } catch (err) {
                console.error('Error fetching services:', err);
                setAvailableServices([]);
            }
        };

        fetchAppointments();
        fetchServices();
    }, [BACKEND_URL]);

    const formatDateTime = (isoString) => {
        if (!isoString) return { date: 'N/A', time: 'N/A' };
        try {
            const dateObj = new Date(isoString);
            if (isNaN(dateObj.getTime())) {
                return { date: 'Invalid Date', time: 'Invalid Time' };
            }
            const date = dateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
            const time = dateObj.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
            return { date, time };
        } catch (error) {
            console.error('Error formatting date:', error, isoString);
            return { date: 'N/A', time: 'N/A' };
        }
    };

    const handleViewReports = () => {
        navigate(`${FRONTEND_URL}/report`);
    };

    const handleSchedule = () => {
        setFormData({
            date: '',
            start_time: '',
            end_time: '',
            status: 'Pending',
            service: '',
            description: '',
            location: ''
        });
        setShowAddModal(true);
    };

    const handleContactAdmin = () => {
        setShowContactModal(true);
    };

    const handleOpenProfileModal = async () => {
        try {
            // Load current user profile data from localStorage or fetch from backend
            const currentUsername = localStorage.getItem('username') || '';
            const currentUserId = localStorage.getItem('user_id') || '';
            
            // Try to fetch from backend if available
            let userData = {
                email: localStorage.getItem('email') || '',
                username: currentUsername,
                first_name: localStorage.getItem('first_name') || '',
                last_name: localStorage.getItem('last_name') || '',
                phone_number: localStorage.getItem('phone_number') || '',
                date_of_birth: localStorage.getItem('date_of_birth') || ''
            };

            // Attempt to fetch fresh data from backend
            try {
                const res = await fetch(`${BACKEND_URL}/users/current`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                });

                const checkedRes = await handleFetchResponse(res, navigate);
                if (checkedRes && checkedRes.ok) {
                    const data = await checkedRes.json();
                    userData = data.user || data || userData;
                }
            } catch (err) {
                console.log('Using localStorage data for profile');
            }

            setProfileData(userData);
            setProfileFormData(userData);
            setShowProfileModal(true);
            setProfileError(null);
            setProfileSuccess(null);
        } catch (err) {
            setProfileError('Failed to load profile');
            console.error('Error loading profile:', err);
        }
    };

    const handleCloseProfileModal = () => {
        setShowProfileModal(false);
        setProfileFormData(profileData);
        setProfileError(null);
        setProfileSuccess(null);
    };

    const handleProfileInputChange = (e) => {
        const { name, value } = e.target;
        setProfileFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitProfile = async () => {
        setProfileLoading(true);
        setProfileError(null);
        setProfileSuccess(null);

        try {
            const response = await fetch(`${BACKEND_URL}/users/update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(profileFormData)
            });

            const checkedResponse = await handleFetchResponse(response, navigate);
            if (!checkedResponse) return;

            if (checkedResponse.ok) {
                setProfileSuccess('Profile updated successfully!');
                setProfileData(profileFormData);
                
                // Update localStorage
                localStorage.setItem('email', profileFormData.email);
                localStorage.setItem('first_name', profileFormData.first_name);
                localStorage.setItem('last_name', profileFormData.last_name);
                localStorage.setItem('phone_number', profileFormData.phone_number);
                localStorage.setItem('date_of_birth', profileFormData.date_of_birth);
                
                // Close modal after 2 seconds
                setTimeout(() => {
                    handleCloseProfileModal();
                }, 2000);
            } else {
                const errorData = await response.json();
                setProfileError(errorData.message || 'Failed to update profile');
            }
        } catch (err) {
            setProfileError('An error occurred while updating profile');
            console.error('Error updating profile:', err);
        } finally {
            setProfileLoading(false);
        }
    };

    const handleOpenUserAdditionalInfoModal = () => {
        setShowUserAdditionalInfoModal(true);
    };

    const handleCloseUserAdditionalInfoModal = () => {
        setShowUserAdditionalInfoModal(false);
    };

    const handleLogout = () => {
        // Clear user data from localStorage
        localStorage.removeItem('username');
        localStorage.removeItem('user_id');
        localStorage.removeItem('role');
        
        // Redirect to login page
        navigate(`${FRONTEND_URL}/login`);
    };

    const handleEditAppointment = (appointment) => {
        setEditingAppointment(appointment);
        const dateTime = new Date(appointment.start_date);
        const endDateTime = appointment.end_date ? new Date(appointment.end_date) : null;
        
        // Handle both old format (service string) and new format (service_names array)
        let services = [];
        if (appointment.service_names && Array.isArray(appointment.service_names)) {
            services = appointment.service_names;
        } else if (appointment.service) {
            services = [appointment.service];
        }
        
        setFormData({
            date: dateTime.toISOString().split('T')[0],
            start_time: dateTime.toTimeString().slice(0, 5),
            end_time: endDateTime ? endDateTime.toTimeString().slice(0, 5) : '',
            status: appointment.status,
            services: services,
            description: appointment.description || '',
            location: appointment.location || ''
        });
        
        setShowEditModal(true);
    };

    const validateAppointmentForm = () => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        // Parse date string in local timezone by splitting and creating date with local time
        const [year, month, day] = formData.date.split('-');
        const selectedDate = new Date(year, month - 1, day);
        const startDateTime = new Date(`${formData.date}T${formData.start_time}`);
        const endDateTime = new Date(`${formData.date}T${formData.end_time}`);
        // Check if date is in the future
        if (selectedDate < today) {
            const errorMsg = 'The appointment date must be in the future';
            setError(errorMsg);
            alert(errorMsg);
            return false;
        }
        
        // Check if start time is in the future
        if (startDateTime <= now) {
            const errorMsg = 'The start time must be in the future';
            setError(errorMsg);
            alert(errorMsg);
            return false;
        }
        
        // Check if end time is after start time
        if (endDateTime <= startDateTime) {
            const errorMsg = 'The end time must be after the start time';
            setError(errorMsg);
            alert(errorMsg);
            return false;
        }

        // Check if location is provided
        if (!formData.location || formData.location.trim() === '') {
            const errorMsg = 'Location is required';
            setError(errorMsg);
            alert(errorMsg);
            return false;
        }
        
        setError(null);
        return true;
    };

    const handleSubmitAdd = async (e) => {
        e.preventDefault();
        
        // Validate the form before proceeding
        if (!validateAppointmentForm()) {
            return;
        }
        
        const startDateTime = new Date(`${formData.date}T${formData.start_time}`);
        const endDateTime = new Date(`${formData.date}T${formData.end_time}`);
        const now = new Date();
        
        // Create appointment object - structure matches database schema
        const newAppointment = {
            start_date: startDateTime.toISOString(),
            end_date: endDateTime.toISOString(),
            status: formData.status,
            services: formData.services,
            description: formData.description,
            location: formData.location
        };

        try {
            // Make POST request to create appointment on backend
            const res = await fetch(`${BACKEND_URL}/appointments/create`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(newAppointment)
            });

            const checkedRes = await handleFetchResponse(res, navigate);
            if (!checkedRes) return;

            if (!checkedRes.ok) {
                throw new Error(`Failed to create appointment: ${checkedRes.status}`);
            }
            
            // Create appointment object with ID for local state (use newAppointment as base)
            const createdAppointment = {
                ...newAppointment,
                id: Date.now().toString() // Generate a temporary ID for local state
            };
            
            // Add to upcoming if future date, otherwise to past
            if (startDateTime >= now) {
                const updated = [...upcomingAppointments, createdAppointment].sort((a, b) => {
                    if (!a || !b || !a.start_date || !b.start_date) return 0;
                    return new Date(a.start_date) - new Date(b.start_date);
                });
                setUpcomingAppointments(updated);
            } else {
                const updated = [...pastAppointments, createdAppointment].sort((a, b) => {
                    if (!a || !b || !a.start_date || !b.start_date) return 0;
                    return new Date(b.start_date) - new Date(a.start_date);
                });
                setPastAppointments(updated);
            }

            // Close modal and reset form
            setShowAddModal(false);
            setFormData({
                date: '',
                start_time: '',
                end_time: '',
                status: 'Pending',
                services: [],
                description: '',
                location: ''
            });
            setError(null);
        } catch (error) {
            console.error('Error creating appointment:', error);
            setError(error.message || 'Failed to create appointment');
            alert(error.message || 'Failed to create appointment');
        }
    };

    const handleSubmitEdit = async (e) => {
        e.preventDefault();
        
        // Validate the form before proceeding
        if (!validateAppointmentForm()) {
            return;
        }
        
        const startDateTime = new Date(`${formData.date}T${formData.start_time}`);
        const endDateTime = new Date(`${formData.date}T${formData.end_time}`);
        const now = new Date();

        const updatedAppointment = {
            ...editingAppointment,
            start_date: startDateTime.toISOString(),
            end_date: endDateTime.toISOString(),
            status: formData.status,
            service_names: formData.services,
            description: formData.description,
            location: formData.location
        };

        try {
            // Make POST request to update appointment on backend
            const res = await fetch(`${BACKEND_URL}/appointments/${editingAppointment.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ "All": updatedAppointment })
            });

            const checkedRes = await handleFetchResponse(res, navigate);
            if (!checkedRes) return;

            if (!checkedRes.ok) {
                throw new Error(`Failed to update appointment: ${checkedRes.status}`);
            }

            // Update the appointment and move it to the correct list if date changed
            const updatedUpcoming = upcomingAppointments.filter(apt => apt.id !== editingAppointment.id);
            const updatedPast = pastAppointments.filter(apt => apt.id !== editingAppointment.id);

            if (startDateTime >= now) {
                setUpcomingAppointments([...updatedUpcoming, updatedAppointment].sort((a, b) => 
                    new Date(a.start_date) - new Date(b.start_date)
                ));
                setPastAppointments(updatedPast);
            } else {
                setPastAppointments([...updatedPast, updatedAppointment].sort((a, b) => 
                    new Date(b.start_date) - new Date(a.start_date)
                ));
                setUpcomingAppointments(updatedUpcoming);
            }

            setShowEditModal(false);
            setEditingAppointment(null);
            setFormData({
                date: '',
                start_time: '',
                end_time: '',
                status: 'Scheduled',
                services: [],
                description: ''
            });
        } catch (error) {
            console.error('Error updating appointment:', error);
            setError(error.message || 'Failed to update appointment');
        }
    };

    const handleCloseModals = () => {
        setShowAddModal(false);
        setShowEditModal(false);
        setEditingAppointment(null);
        setFormData({
            date: '',
            start_time: '',
            end_time: '',
            status: 'Pending',
            services: [],
            description: '',
            location: ''
        });
    };

    const updateMenuPosition = (appointmentId, containerElement) => {
        if (!containerElement) return;
        
        setTimeout(() => {
            try {
                // Find the button element within the container
                const buttonElement = containerElement.querySelector('.details-button');
                if (!buttonElement) return;
                
                const buttonRect = buttonElement.getBoundingClientRect();
                const menuHeight = 150; // Approximate menu height
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - buttonRect.bottom;
                const spaceAbove = buttonRect.top;
                
                let top = buttonRect.bottom + 8;
                const right = window.innerWidth - buttonRect.right;
                
                // If not enough space below but enough space above, open upward
                if (spaceBelow < menuHeight + 20 && spaceAbove > menuHeight + 20) {
                    top = buttonRect.top - menuHeight - 8;
                }
                
                setMenuPosition({ top, right });
            } catch (error) {
                console.error('Error positioning menu:', error);
            }
        }, 10);
    };

    const handleMouseEnter = (appointmentId, e) => {
        if (!appointmentId) {
            console.error('Appointment ID is missing');
            return;
        }
        
        // Clear any pending hide timeout
        if (menuHideTimeoutRef.current) {
            clearTimeout(menuHideTimeoutRef.current);
            menuHideTimeoutRef.current = null;
        }
        
        if (e) {
            e.stopPropagation();
        }
        
        setShowDetailsMenu(appointmentId);
        updateMenuPosition(appointmentId, e?.currentTarget);
    };

    const handleMouseLeave = () => {
        // Add a delay before hiding the menu (300ms) to give user time to move to menu
        menuHideTimeoutRef.current = setTimeout(() => {
            setShowDetailsMenu(null);
            menuHideTimeoutRef.current = null;
        }, 300);
    };

    const handleCalendarAppointmentClick = (appointment, e) => {
        if (e) e.stopPropagation();
        setSelectedAppointment(appointment);
        setShowAppointmentDetails(true);
    };

    const handleCloseAppointmentDetails = () => {
        setShowAppointmentDetails(false);
        setSelectedAppointment(null);
    };

    const handleCloseDetailsMenu = () => {
        setShowDetailsMenu(null);
    };

    const handleMarkAsCompleted = async (appointment) => {
        try {
            const updatedAppointment = {
                ...appointment,
                status: 'serviced'
            };

            const res = await fetch(`${BACKEND_URL}/appointments/${appointment.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({Status: { status: 'serviced' }})
            });

            const checkedRes = await handleFetchResponse(res, navigate);
            if (!checkedRes) return;

            if (!checkedRes.ok) {
                throw new Error(`Failed to update appointment: ${checkedRes.status}`);
            }

            // Update the appointment in state
            const updatedUpcoming = upcomingAppointments.filter(apt => apt.id !== appointment.id);
            const updatedPast = pastAppointments.filter(apt => apt.id !== appointment.id);

            const now = new Date();
            if (new Date(appointment.start_date) >= now) {
                setUpcomingAppointments([...updatedUpcoming, updatedAppointment].sort((a, b) => 
                    new Date(a.start_date) - new Date(b.start_date)
                ));
            } else {
                setPastAppointments([...updatedPast, updatedAppointment].sort((a, b) => 
                    new Date(b.start_date) - new Date(a.start_date)
                ));
            }

            setShowDetailsMenu(null);
        } catch (err) {
            console.error('Error marking appointment as completed:', err);
            alert(`Error: ${err.message}`);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (showDetailsMenu) {
                const containerElement = document.querySelector(
                    `.details-menu-container[data-appointment-id="${showDetailsMenu}"]`
                );
                if (containerElement) {
                    updateMenuPosition(showDetailsMenu, containerElement);
                }
            }
        };

        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            // Clean up timeout on unmount
            if (menuHideTimeoutRef.current) {
                clearTimeout(menuHideTimeoutRef.current);
            }
        };
    }, [showDetailsMenu]);

    // Filter upcoming appointments by status and service
    useEffect(() => {
        let filtered = upcomingAppointments;

        if (upcomingStatusFilter !== 'all') {
            filtered = filtered.filter(apt => apt.status === upcomingStatusFilter);
        }

        if (upcomingServiceFilter !== 'all') {
            filtered = filtered.filter(apt => {
                const service = apt.service_name || apt.service;
                return service === upcomingServiceFilter;
            });
        }

        // Store filtered results - for future use if needed
        // For now, we'll just use upcomingAppointments directly in the UI and filter there
    }, [upcomingAppointments, upcomingStatusFilter, upcomingServiceFilter]);

    const getStatusBadgeClass = (status) => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            case 'pending':
                return 'status-badge status-pending';
            case 'assigned':
                return 'status-badge status-assigned';
            case 'accepted':
                return 'status-badge status-accepted';
            case 'rejected':
                return 'status-badge status-rejected';
            case 'serviced':
                return 'status-badge status-serviced';
            case 'reviewed':
                return 'status-badge status-reviewed';
            case 'completed':
                return 'status-badge status-completed';
            case 'cancelled':
                return 'status-badge status-cancelled';
            default:
                return 'status-badge';
        }
    };

    // Calendar view functions
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        
        const days = [];
        // Add empty cells for days before the first day of the month
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }
        // Add all days of the month
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        return days;
    };

    const getAppointmentsForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split('T')[0];
        // Filter and sort appointments for the given date
        // This works with any data structure from database as long as it has start_date
        return upcomingAppointments
            .filter(apt => {
                if (!apt || !apt.start_date) return false;
                try {
                    const aptDate = new Date(apt.start_date).toISOString().split('T')[0];
                    return aptDate === dateStr;
                } catch (e) {
                    return false;
                }
            })
            .sort((a, b) => {
                // Sort by time - handles database timestamps
                try {
                    const timeA = new Date(a.start_date).getTime();
                    const timeB = new Date(b.start_date).getTime();
                    return timeA - timeB;
                } catch (e) {
                    return 0;
                }
            });
    };

    const getStatusInfo = (status) => {
        const statusLower = status ? status.toLowerCase() : '';
        if (statusLower === 'serviced' || statusLower === 'completed' || statusLower === 'reviewed') {
            return { label: status, class: 'status-success', color: '#10b981' };
        } else if (statusLower === 'pending' || statusLower === 'assigned' || statusLower === 'accepted') {
            return { label: status, class: 'status-warning', color: '#f59e0b' };
        } else if (statusLower === 'rejected' || statusLower === 'cancelled') {
            return { label: status, class: 'status-danger', color: '#ef4444' };
        }
        return { label: status || 'Unknown', class: '', color: '#64748b' };
    };

    const navigateMonth = (direction) => {
        setCurrentMonth(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + direction);
            return newDate;
        });
    };

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    if (loading) {
        return (
            <div className="dashboard-page">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p className="loading-message">Loading appointments...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <DashboardSidebar
                sidebarCollapsed={sidebarCollapsed}
                onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                mobileMenuOpen={mobileMenuOpen}
                onToggleMobileMenu={() => setMobileMenuOpen(!mobileMenuOpen)}
                profileTitle={`Welcome Back${user.username ? `, ${user.username}` : ''}`}
                profileSubtitle="Manage your appointments"
                buttons={[
                    {
                        icon: '📊',
                        text: 'View Reports',
                        onClick: () => {
                            handleViewReports();
                            setMobileMenuOpen(false);
                        },
                        ariaLabel: 'View reports',
                        title: 'View Reports'
                    },
                    {
                        icon: '➕',
                        text: 'Schedule Appointment',
                        onClick: () => {
                            handleSchedule();
                            setMobileMenuOpen(false);
                        },
                        ariaLabel: 'Schedule new appointment',
                        title: 'Schedule Appointment'
                    },
                    {
                        icon: '✉️',
                        text: 'Contact Admin',
                        onClick: () => {
                            handleContactAdmin();
                            setMobileMenuOpen(false);
                        },
                        ariaLabel: 'Contact admin',
                        title: 'Contact Admin'
                    },
                    {
                        icon: '👤',
                        text: 'Profile',
                        onClick: () => {
                            handleOpenProfileModal();
                            setMobileMenuOpen(false);
                        },
                        ariaLabel: 'Profile',
                        title: 'Profile'
                    },
                    {
                        icon: '📄',
                        text: 'Additional Info',
                        onClick: () => {
                            handleOpenUserAdditionalInfoModal();
                            setMobileMenuOpen(false);
                        },
                        ariaLabel: 'Additional information',
                        title: 'Additional Information'
                    },
                    {
                        icon: '🚪',
                        text: 'Logout',
                        onClick: () => {
                            handleLogout();
                            setMobileMenuOpen(false);
                        },
                        className: 'logout-button',
                        ariaLabel: 'Logout',
                        title: 'Logout'
                    }
                ]}
            />

            <div className="dashboard-main">
                <section className="dashboard-card">
                    <div className="dashboard-title-row">
                        <h3>Upcoming Appointments</h3>
                        <div className="title-actions">
                            <button
                                className="view-toggle-button"
                                onClick={() => setCalendarView(!calendarView)}
                                aria-label={calendarView ? "Switch to list view" : "Switch to calendar view"}
                            >
                                {calendarView ? '📋 List' : '📅 Calendar'}
                            </button>
                            <small>{upcomingAppointments.length} items</small>
                        </div>
                    </div>

                    {/* Filter Section */}
                    <div style={{ marginBottom: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ fontWeight: '600' }}>Filter by:</label>
                            <select
                                value={upcomingStatusFilter}
                                onChange={(e) => setUpcomingStatusFilter(e.target.value)}
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                            >
                                <option value="all">All Statuses</option>
                                {availableStatuses.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>

                            <select
                                value={upcomingServiceFilter}
                                onChange={(e) => setUpcomingServiceFilter(e.target.value)}
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                            >
                                <option value="all">All Services</option>
                                {availableServices.map(service => (
                                    <option key={service} value={service}>{service}</option>
                                ))}
                            </select>

                            {(upcomingStatusFilter !== 'all' || upcomingServiceFilter !== 'all') && (
                                <button
                                    onClick={() => {
                                        setUpcomingStatusFilter('all');
                                        setUpcomingServiceFilter('all');
                                    }}
                                    style={{
                                        padding: '8px 12px',
                                        background: '#e2e8f0',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    </div>
                    {calendarView ? (
                        <div className="calendar-container">
                            <div className="calendar-header">
                                <button 
                                    className="calendar-nav-button"
                                    onClick={() => navigateMonth(-1)}
                                    aria-label="Previous month"
                                >
                                    ‹
                                </button>
                                <h4 className="calendar-month-year">
                                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                                </h4>
                                <button 
                                    className="calendar-nav-button"
                                    onClick={() => navigateMonth(1)}
                                    aria-label="Next month"
                                >
                                    ›
                                </button>
                            </div>
                            <div className="calendar-legend">
                                <div className="legend-item">
                                    <div className="legend-color status-success"></div>
                                    <span>Completed</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color status-warning"></div>
                                    <span>Pending</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color status-danger"></div>
                                    <span>Cancelled</span>
                                </div>
                            </div>
                            <div className="calendar-grid">
                                {dayNames.map(day => (
                                    <div key={day} className="calendar-day-header">{day}</div>
                                ))}
                                {getDaysInMonth(currentMonth).map((date, index) => {
                                    const appointments = getAppointmentsForDate(date);
                                    const isToday = date && 
                                        date.toDateString() === new Date().toDateString();
                                    const isWeekend = date && (date.getDay() === 0 || date.getDay() === 6);
                                    
                                    return (
                                        <div 
                                            key={index} 
                                            className={`calendar-day ${!date ? 'empty' : ''} ${isToday ? 'today' : ''} ${isWeekend ? 'weekend' : ''}`}
                                        >
                                            {date && (
                                                <>
                                                    <div className="calendar-day-number">{date.getDate()}</div>
                                                    <div className="calendar-day-appointments">
                                                        {appointments.slice(0, 3).map(apt => {
                                                            if (!apt) return null;
                                                            const { time } = formatDateTime(apt.start_date);
                                                            const statusClass = getStatusBadgeClass(apt.status).split(' ')[1] || '';
                                                            return (
                                                                <div 
                                                                    key={apt.id}
                                                                    className={`calendar-appointment ${statusClass}`}
                                                                    onClick={(e) => handleCalendarAppointmentClick(apt, e)}
                                                                    title={`${time} - ${apt.psw_name || 'N/A'}`}
                                                                >
                                                                    <span className="appointment-time">{time}</span>
                                                                    <span className="appointment-psw">{apt.psw_name || 'N/A'}</span>
                                                                </div>
                                                            );
                                                        })}
                                                        {appointments.length > 3 && (
                                                            <div className="calendar-more-appointments">
                                                                +{appointments.length - 3} more
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="dashboard-table" role="table" aria-label="Upcoming appointments">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Caregiver</th>
                                    <th>Service</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {upcomingAppointments.filter(a => {
                                    let matchesStatus = upcomingStatusFilter === 'all' || a.status === upcomingStatusFilter;
                                    let matchesService = upcomingServiceFilter === 'all' || getServiceNames(a) === upcomingServiceFilter || 
                                        (a.service_names && Array.isArray(a.service_names) && a.service_names.includes(upcomingServiceFilter));
                                    return matchesStatus && matchesService;
                                }).map((a, index) => {
                                    const { date, time } = formatDateTime(a.start_date);
                                    
                                    return (
                                        <tr key={a.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                            <td>{date}</td>
                                            <td>{time}</td>
                                            <td className="caregiver-id">{a.psw_name}</td>
                                            <td>{getServiceNames(a)}</td>
                                            <td>
                                                <span className={getStatusBadgeClass(a.status)}>
                                                    {a.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="actions-cell">
                                                    <div 
                                                        className="details-menu-container"
                                                        data-appointment-id={a.id}
                                                        onMouseEnter={(e) => handleMouseEnter(a.id, e)}
                                                        onMouseLeave={handleMouseLeave}
                                                    >
                                                        <button
                                                            className="details-button"
                                                            aria-label="View details"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
                                                            </svg>
                                                        </button>
                                                        {showDetailsMenu === a.id && (
                                                            <div 
                                                                className="details-menu"
                                                                style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                                                                onMouseEnter={() => {
                                                                    // Clear any pending hide timeout when hovering over menu
                                                                    if (menuHideTimeoutRef.current) {
                                                                        clearTimeout(menuHideTimeoutRef.current);
                                                                        menuHideTimeoutRef.current = null;
                                                                    }
                                                                    setShowDetailsMenu(a.id);
                                                                }}
                                                                onMouseLeave={handleMouseLeave}
                                                            >
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        handleEditAppointment(a);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">✏️</span>
                                                                    Edit Appointment
                                                                </button>
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        setSelectedAppointment(a);
                                                                        setShowAppointmentDetails(true);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">👁️</span>
                                                                    View Full Details
                                                                </button>
                                                                {a.status && a.status.toLowerCase() === 'accepted' && (
                                                                    <button
                                                                        className="details-menu-item"
                                                                        onClick={() => {
                                                                            handleMarkAsCompleted(a);
                                                                        }}
                                                                    >
                                                                        <span className="menu-icon">✅</span>
                                                                        Mark as Completed
                                                                    </button>
                                                                )}
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">❌</span>
                                                                    Cancel Appointment
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {upcomingAppointments.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="empty-state">
                                            <div className="empty-state-content">
                                                <span className="empty-icon">📅</span>
                                                <p>No upcoming appointments</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    )}
                </section>

                <section className="dashboard-card">
                    <div className="dashboard-title-row">
                        <h3>Past Appointments</h3>
                        <small>{pastAppointments.length} items</small>
                    </div>
                    <div className="table-container">
                        <table className="dashboard-table" role="table" aria-label="Past appointments">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Caregiver</th>
                                    <th>Service</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pastAppointments.map((a, index) => {
                                    if (!a || !a.id) return null;
                                    const { date, time } = formatDateTime(a.start_date);
                                    return (
                                        <tr key={a.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                            <td>{date}</td>
                                            <td>{time}</td>
                                            <td className="caregiver-id">{a.psw_name || 'N/A'}</td>
                                            <td>{getServiceNames(a)}</td>
                                            <td>
                                                <span className={getStatusBadgeClass(a.status)}>
                                                    {a.status || 'N/A'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="actions-cell">
                                                    <div 
                                                        className="details-menu-container" 
                                                        data-appointment-id={a.id}
                                                        onMouseEnter={(e) => handleMouseEnter(a.id, e)}
                                                        onMouseLeave={handleMouseLeave}
                                                    >
                                                        <button
                                                            className="details-button"
                                                            aria-label="View details"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
                                                            </svg>
                                                        </button>
                                                        {showDetailsMenu === a.id && (
                                                            <div 
                                                                className="details-menu"
                                                                style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                                                                onMouseEnter={() => {
                                                                    // Clear any pending hide timeout when hovering over menu
                                                                    if (menuHideTimeoutRef.current) {
                                                                        clearTimeout(menuHideTimeoutRef.current);
                                                                        menuHideTimeoutRef.current = null;
                                                                    }
                                                                    setShowDetailsMenu(a.id);
                                                                }}
                                                                onMouseLeave={handleMouseLeave}
                                                            >
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        setSelectedAppointment(a);
                                                                        setShowAppointmentDetails(true);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">👁️</span>
                                                                    View Full Details
                                                                </button>
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        setSelectedReportId(a.report_id || a.id);
                                                                        setShowReportModal(true);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">📄</span>
                                                                    View Report
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {pastAppointments.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="empty-state">
                                            <div className="empty-state-content">
                                                <span className="empty-icon">📋</span>
                                                <p>No past appointments</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            {/* Appointment Scheduler Modal */}
            <AppointmentSchedulerModal
                isOpen={showAddModal || showEditModal}
                title={showEditModal ? "Edit Appointment" : "Schedule New Appointment"}
                formData={formData}
                onFormChange={setFormData}
                onSubmit={showEditModal ? handleSubmitEdit : handleSubmitAdd}
                onClose={handleCloseModals}
                availableServices={availableServices}
                isLoading={false}
            />

            {/* Appointment Details Modal */}
            <AppointmentDetailsModal
                isOpen={showAppointmentDetails}
                appointment={selectedAppointment}
                onClose={handleCloseAppointmentDetails}
                formatDateTime={formatDateTime}
                showCaregiver={true}
                getStatusBadgeClass={getStatusBadgeClass}
                onEditAppointment={handleEditAppointment}
                isUpcomingAppointment={selectedAppointment && new Date(selectedAppointment.start_date) >= new Date()}
            />

            <ContactAdminModal 
                isOpen={showContactModal} 
                onClose={() => setShowContactModal(false)} 
            />

            <ReportModal 
                isOpen={showReportModal}
                onClose={() => {
                    setShowReportModal(false);
                    setSelectedReportId(null);
                }}
                reportId={selectedReportId}
            />

            {/* Profile Modal */}
            <ProfileModal
                showModal={showProfileModal}
                onClose={handleCloseProfileModal}
                profileFormData={profileFormData}
                onInputChange={handleProfileInputChange}
                onSubmit={handleSubmitProfile}
                loading={profileLoading}
                error={profileError}
                success={profileSuccess}
            />

            {/* User Additional Info Modal */}
            <UserAdditionalInfoModal
                isOpen={showUserAdditionalInfoModal}
                onClose={handleCloseUserAdditionalInfoModal}
                userId={user.user_id}
            />
        </div>
    );
}


